import { HtmlMarkerInstanceProps, HtmlMarkerSetProps } from '@fabric-web/3d-viewer';
import { useAtomValue } from 'jotai';
import {
  hoveredCustomBlock as hoveredCustomBlockAtom,
  hoveredCustomBlockDefects as hoveredCustomBlockDefectsAtom,
} from '../state';
import {
  CustomBlockHighlightMarker,
  CustomBlockHighlightMarkerProps,
} from '../components/CustomBlockHighlightMarker';
import { useMemo } from 'react';
import { CustomBlockDefectsByPaintBlock } from '@/types';
import { UnitsEnum, UnitSystemEnum, PointOfInterest } from '@/__generated__/graphql';
import { unitSystem as unitSystemAtom } from '@/components/Analysis/state';
import { roundNumber, SQFEET_TO_SQMETERS_FACTOR, localiseMeasurement } from '@/utils/unitSystem';

const getRoundedDefectValue = ({
  defectsArray,
  type,
  unitSystem,
}: {
  defectsArray: CustomBlockDefectsByPaintBlock[0]['totalArea'] | undefined;
  type: string;
  unitSystem: UnitSystemEnum;
}): number | undefined => {
  const found = defectsArray?.find((item) => item?.type === type);
  if (!found?.value) return;
  if (typeof found?.value !== 'number') return;
  const converted =
    unitSystem === UnitSystemEnum.Imperial ? found.value * SQFEET_TO_SQMETERS_FACTOR : found.value;
  return roundNumber(converted, 2);
};

export const useCustomBlockHighlightMarkers = () => {
  const hoveredCustomBlock = useAtomValue(hoveredCustomBlockAtom);
  const hoveredCustomBlockDefects = useAtomValue(hoveredCustomBlockDefectsAtom);
  const unitSystem = useAtomValue(unitSystemAtom);

  const instances: HtmlMarkerInstanceProps[] = useMemo(() => {
    if (!hoveredCustomBlock || !hoveredCustomBlockDefects) return [];

    let totalSurfaceAreaDisplay: string | undefined;
    let ri4plusri5Display: string | undefined;
    const unitLabel = unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²';

    if ('totalArea' in hoveredCustomBlockDefects) {
      // Custom block: use totalArea array
      const defectsArray = hoveredCustomBlockDefects.totalArea;

      const totalSurfaceArea = getRoundedDefectValue({
        defectsArray,
        type: 'area',
        unitSystem,
      });
      const ri4plusri5 = getRoundedDefectValue({
        defectsArray,
        type: 'Ri4+Ri5',
        unitSystem,
      });

      totalSurfaceAreaDisplay = totalSurfaceArea ? `${totalSurfaceArea} ${unitLabel}` : undefined;
      ri4plusri5Display = ri4plusri5 ? `${ri4plusri5} ${unitLabel}` : undefined;
    } else {
      // Saved block: extract from template/templateData
      const savedBlock = hoveredCustomBlockDefects as PointOfInterest;

      totalSurfaceAreaDisplay = savedBlock.templateFieldData.totalArea
        ? localiseMeasurement({
            unitSystem,
            value: savedBlock.templateFieldData.totalArea,
            sourceUnit: UnitsEnum.SquareMeter,
            showUnitLabel: true,
            roundToDecimal: 2,
          })
        : undefined;
      ri4plusri5Display = savedBlock.templateFieldData.ri4plusri5
        ? localiseMeasurement({
            unitSystem,
            value: savedBlock.templateFieldData.ri4plusri5,
            sourceUnit: UnitsEnum.SquareMeter,
            showUnitLabel: true,
            roundToDecimal: 2,
          })
        : undefined;
    }
    const props: CustomBlockHighlightMarkerProps = {
      title: hoveredCustomBlock.name,
      data: [
        { label: 'Total surface area', value: totalSurfaceAreaDisplay },
        { label: 'Ri4 & Ri5', value: ri4plusri5Display },
      ],
    };

    return [
      {
        id: hoveredCustomBlock.id,
        position: hoveredCustomBlock.position,
        children: <CustomBlockHighlightMarker {...props} />,
        distanceThreshold: 1000,
        scale: 7,
      },
    ];
  }, [hoveredCustomBlock, hoveredCustomBlockDefects, unitSystem]);

  const markerSet: HtmlMarkerSetProps = useMemo(
    () => ({
      id: 'custom-blocks-highlight-markers',
      instances,
    }),
    [instances]
  );

  return { markerSet };
};
